import * as React from 'react';
import { useFormContext } from 'react-hook-form';
/**
 * Черновик текстового ввода поверх значения формы — общий для
 * `NumberWidget` и `DecimalWidget`.
 *
 * Пока оператор правит поле, авторитетен НАБРАННЫЙ ТЕКСТ, а не значение
 * формы: промежуточное состояние ввода («пусто», «-», «1.», «1,») не обязано
 * быть валидным числом. Без черновика стереть последнюю цифру было
 * невозможно — react-hook-form читает значение поля через
 * `get(values, name, defaultValue)`, а тот на `undefined` отдаёт
 * `defaultValues`, и поле тут же возвращало прежнее число (чтобы заменить 6
 * на 4, приходилось набирать 46 и стирать 6). В сами `_formValues` при этом
 * ложится `undefined`, поэтому пустое поле — ошибка валидации при сохранении,
 * а не тихая запись старого числа.
 *
 * Значение поменялось снаружи (`form.reset`, программный `setValue`) —
 * черновик устарел и уступает. Правка самого оператора сюда не попадает:
 * там значение формы совпадает с разобранным черновиком (`parse(draft)`).
 *
 * «Значение формы» здесь — ХРАНИМОЕ значение (`getValues(name)`), а не
 * `field.value`: для стёртого поля (`undefined`) `field.value` — это
 * `defaultValues`, т. е. оно меняется (последняя цифра → умолчание), хотя
 * снаружи никто ничего не трогал. Сравнение по `field.value` принимало это
 * за сброс, роняло черновик и возвращало в поле умолчание — стоило стереть
 * правленое значение посимвольно, и набор дописывался к умолчанию («100» →
 * стереть → «150» = «100150»). Вне `FormProvider` (юнит-тесты хука) хранимого
 * значения нет — тогда `field.value`.
 *
 * До выноса эта логика жила двумя копиями в двух виджетах — тонкий
 * инвариант, который разошёлся бы при первой правке одной из них.
 */
export function useDraftValue(field, parse, format) {
    const [draft, setDraft] = React.useState(null);
    // Без провайдера react-hook-form отдаёт null, хотя тип этого не говорит.
    const form = useFormContext();
    const stored = form && field.name ? form.getValues(field.name) : field.value;
    const seenValue = React.useRef(stored);
    if (!Object.is(seenValue.current, stored)) {
        const stale = draft !== null && !Object.is(parse(draft), stored);
        seenValue.current = stored;
        if (stale)
            setDraft(null);
    }
    return {
        // Без черновика — тоже хранимое значение: стёртое поле, смонтированное
        // заново (черновик потерян), пусто, а не показывает умолчание, которое
        // при сохранении в форму не попадёт.
        value: draft ?? format(stored),
        onChange: (raw) => {
            setDraft(raw);
            field.onChange(parse(raw));
        },
    };
}
//# sourceMappingURL=use-draft-value.js.map